import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '../../hooks/useQuery';
import { Breadcrumbs } from '../../components/navigation/Breadcrumbs';
import { Loader2, Library, User } from 'lucide-react';
import { FilterBar } from '../../components/filters/FilterBar';
import { useCards } from '../../hooks/useCards';
import { FilterState, PokemonCard, UserCard } from '../../types/pokemon';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { Avatar } from '../../components/common/Avatar';
import { UserCollectionCardGrid } from '../../components/cards/UserCollectionCardGrid';
import { useI18n } from '../../contexts/I18nContext';

interface Profile {
  user_id: string;
  username: string;
  is_collection_public: boolean;
  avatar_url?: string | null;
  [key: string]: string | number | boolean | null | undefined;
}

export function UserCollection() {
  const navigate = useNavigate();
  const { username } = useParams<{ username: string }>();
  const { t, getPathForLang } = useI18n();
  const [filters, setFilters] = useState<FilterState>({
    pack: null,
    rarity: null,
    set: null,
    search: '',
    type: null,
    energy: null,
    showMissing: false
  });

  const { data: profile, loading: profileLoading } = useQuery<Profile>(
    'profiles',
    { username },
    { single: true }
  );

  const { data: userCards } = useQuery<UserCard[]>(
    'user_cards',
    { user_id: profile?.user_id },
    { withRelations: 'card_id, quantity' }
  );

  const {
    cards,
    loading: cardsLoading,
    filterOptions: { packs = [], rarities = [], sets = [], types = [], energies = [] },
    hasMore,
    isLoadingMore,
    loadMore
  } = useCards(filters);

  // Filtrer les cartes pour n'afficher que celles possédées par l'utilisateur
  const filteredCards = cards.filter(card => 
    userCards?.some(userCard => userCard.card_id === card.id && userCard.quantity > 0) || false
  );

  // Ajoutez cette fonction de gestion de la navigation
  const handleCardClick = (card: PokemonCard) => {
    navigate(getPathForLang(`/sets/${card.set}/cards/${card.id}`));
  };

  // Initialize infinite scroll
  useInfiniteScroll(loadMore, hasMore && !isLoadingMore);

  if (profileLoading || cardsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="sr-only">{t('user_collection.loading')}</span>
      </div>
    );
  }

  if (!profile?.is_collection_public) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <Library className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('user_collection.private.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('user_collection.private.message')}
          </p>
        </div>
      </div>
    );
  }

  // Convertir userCards en Record<number, UserCard>
  const userCardsRecord: Record<number, UserCard> = {};
  userCards?.forEach(card => {
    userCardsRecord[card.card_id] = card;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs
        items={[
          { label: t('friend_codes.breadcrumbs.friend_codes'), path: getPathForLang('/friend-codes') },
          { label: t('user_collection.breadcrumbs.collection', { username }), path: getPathForLang(`/users/${username}/collection`) }
        ]}
      />

      <div className="mt-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative">
              <Avatar 
                url={profile?.avatar_url} 
                username={username || ''} 
                size="lg" 
                className="w-24 h-24 border-4 border-white shadow-lg"
              />
            </div>
            <div className="flex-1 flex flex-col items-center sm:items-start">
              <h1 className="text-3xl font-bold text-gray-900">
                {t('user_collection.title', { username })}
              </h1>
              <div className="mt-3 flex items-center">
                <Link 
                  to={getPathForLang(`/users/${username}`)} 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100 transition-colors font-medium text-sm"
                >
                  <User className="w-4 h-4" />
                  <span>{t('user_collection.profile.view_profile')}</span>
                </Link>
                <div className="ml-4 px-4 py-2 bg-gray-50 text-gray-700 rounded-full font-medium text-sm">
                  {t('user_collection.profile.cards_count', { count: Object.values(userCardsRecord).reduce((sum, card) => sum + (card.quantity || 0), 0) })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <FilterBar
          filters={filters}
          onFilterChange={setFilters}
          packs={packs || []}
          rarities={rarities || []}
          sets={sets || []}
          types={types || []}
          energies={energies || []}
          showMissingToggle={false}
        />

        <div className="mt-6">
          <UserCollectionCardGrid
            cards={filteredCards}
            loading={cardsLoading}
            collection={userCardsRecord}
            onCardClick={handleCardClick}
          />
          
          {isLoadingMore && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <span className="sr-only">{t('user_collection.loading')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}